<!-- eslint-disable no-use-before-define -->
<script setup lang="ts">
// TODO : check out of this new popover API (available in all browsers now) is better than this custom float component
// https://developer.mozilla.org/en-US/docs/Web/API/Popover_API

import { ref, computed, onUnmounted, type HTMLAttributes } from 'vue'
import {
  fl_getPositionStyle,
  fl_getVirtualEl,
  fl_followMouse,
} from '@/components/lib/GwUtils/utils_float'
import {
  addEventListeners,
  removeEventListeners,
  type Listener,
} from '@/components/lib/GwUtils/utils_listeners'

export type FloatPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'contextmenu'

const ignoredAttrs = [
  'data-gwmenu-noclose', //
  'data-gwtooltip-noclose',
]
export type GwFloatIgnoredAttrs = (typeof ignoredAttrs)[number]

export interface FloatProps {
  trigger: 'click' | 'hover'
  disabled?: boolean
  isMovable?: boolean
  closeOnContentClick?: boolean
  closeOnContentHover?: boolean
  closeOnClick?: boolean
  // https://floating-ui.com/
  placement?: FloatPlacement
  boundary?: HTMLElement
  closeOnScroll?: boolean
  customPositionStyle?:
    | {
        top?: string
        right?: string
        bottom?: string
        left?: string
        transform?: string
      }
    | undefined
}

export type GwFloatMethods = {
  open: (e: any) => void
  close: () => void
}

const props = withDefaults(defineProps<FloatProps>(), {
  trigger: 'hover',
  closeOnContentHover: true,
  placement: 'right-start',
})

const openId = ref('')
const positionStyle = ref({})

let openerEl: any
let contentEl: any

// moving
let mousedownOnContent = false
let offsetX: undefined | number
let offsetY: undefined | number

const emit = defineEmits(['open', 'close'])

onUnmounted(() => {
  setupClickListeners(false)
})

const open = (event?: any) => {
  const el = event && (event.relatedTarget || event.toElement || event.target)

  if (event && openId.value && getCursorInside(el) === 'opener') {
    event.preventDefault()
    close()
    return
  }

  if (props.disabled) return

  openId.value = `${Math.floor(Date.now() + Math.random())}`

  const cspStyle = props.customPositionStyle
  if (cspStyle) {
    requestAnimationFrame(() => {
      contentEl = document.querySelector(`[data-floatcontent="${openId.value}"]`)
      contentEl.style.position = 'fixed'
      contentEl.style.top = cspStyle.top || 'auto'
      contentEl.style.right = cspStyle.right || 'auto'
      contentEl.style.bottom = cspStyle.bottom || 'auto'
      contentEl.style.left = cspStyle.left || 'auto'
      handleOpened()
    })
    return
  }

  if (!event) {
    // eslint-disable-next-line no-console
    console.warn('MouseEvent is required for this usage of GwFloat!!')
    return
  }
  openerEl = event.currentTarget || event.target || openerEl
  const referenceEl = props.placement === 'contextmenu' ? fl_getVirtualEl({ e: event }) : openerEl

  requestAnimationFrame(async () => {
    contentEl = document.querySelector(`[data-floatcontent="${openId.value}"]`)
    if (!openerEl || !contentEl) return

    openerEl.setAttribute('data-floatid', openId.value)

    const params = {
      referenceEl,
      element: contentEl,
      options: {
        strategy: 'fixed',
        placement: props.placement,
        offset: 0,
      },
      boundary: props.boundary || document.getElementById('gw_main'),
    }
    const { position, left, top, maxHeight } = await fl_getPositionStyle(params)

    positionStyle.value = {
      position,
      left,
      top,
      maxHeight,
    }

    handleOpened()
  })
}

const close = () => {
  openId.value = ''
  if (openerEl) {
    openerEl.removeAttribute('data-floatid')
    openerEl = undefined
  }
  mousedownOnContent = false
  offsetX = undefined
  offsetY = undefined
  handleClosed()
}
defineExpose({
  open,
  close,
})

const getCursorInside: any = (el: Element) => {
  if (!el) return ''
  if (
    el.getAttribute('data-floatcontent') === openId.value ||
    ignoredAttrs.some(attr => el.getAttribute(attr))
  ) {
    return 'content'
  }
  if (el?.getAttribute('data-floatid') === openId.value) {
    return 'opener'
  }
  if (el.parentElement) {
    return getCursorInside(el.parentElement)
  }
  return ''
}

const handleClose = (e: any) => {
  // any cos Firefox has only toElement and that is not on MouseEvent
  const el = e.relatedTarget || e.toElement || e.target
  const layer = getCursorInside(el)

  if (layer === 'opener') {
    return
  }
  if (props.trigger === 'click' && !props.closeOnContentClick && layer === 'content') {
    return
  }
  if (props.trigger === 'hover' && !props.closeOnContentHover && layer === 'content') {
    return
  }
  close()
}

const setupClickListeners = (b: boolean) => {
  const fn = b ? addEventListeners : removeEventListeners
  fn(
    [
      props.isMovable && [document, 'mousemove', move, true], // isMovable
      [document, 'mousedown', handleClose, true],
      [document, 'touchstart', handleClose, true],
      [document, 'touchmove', handleClose, true],
      props.closeOnScroll && [document, 'scroll', handleClose, true],
    ].filter(Boolean) as Listener[],
  )
}
const handleContentMousedown = () => {
  mousedownOnContent = true
}

const move = (e: any) => {
  if (!mousedownOnContent) return

  e.preventDefault()
  const boundary = props.boundary || document
  contentEl = document.querySelector(`[data-floatcontent="${openId.value}"]`)
  if (!contentEl || !boundary) return

  if (offsetX === undefined && offsetY === undefined) {
    const { x, y } = contentEl.getBoundingClientRect()
    offsetX = x - e.clientX
    offsetY = y - e.clientY
  }

  fl_followMouse(
    e,
    boundary,
    contentEl,
    {
      offsetY,
      offsetX,
      padding: 0,
      strategy: 'absolute',
    },
    {
      has_flip: false,
      has_shift: false,
    },
  )
}

const handleClosed = () => {
  setupClickListeners(false)
  emit('close')
}
const handleOpened = () => {
  if (
    props.trigger === 'click' || //
    props.closeOnClick ||
    props.closeOnContentClick
  ) {
    setupClickListeners(true)
  }
  emit('open')
}

const contentAttrs = computed<HTMLAttributes>(() => ({
  'data-floatcontent': openId.value,
  style: {
    position: 'fixed',
    ...positionStyle.value,
  },
}))

const methods: GwFloatMethods = {
  open,
  close,
}

const openerListeners =
  props.trigger === 'click'
    ? {
        click: open,
      }
    : {
        ...(props.closeOnClick ? { click: close } : {}),
        mouseenter: open,
        mouseleave: handleClose,
      }
const contentListeners =
  props.trigger === 'click'
    ? { mousedown: handleContentMousedown }
    : {
        mouseleave: handleClose,
        mousedown: handleContentMousedown,
      }
</script>
<template>
  <slot
    name="opener"
    :listeners="openerListeners"
    :openId
    :methods
  >
    <!-- <div
      style="width: 50px; background-color: red"
      v-on="openerListeners"
    >
      opener
    </div> -->
  </slot>
  <slot
    name="default"
    :attrs="contentAttrs"
    :listeners="contentListeners"
    :openId
    :methods
  >
    <!-- <div
        v-bind="contentAttrs"
        style="background-color: blue"
        v-on="contentListeners"
      >
        content
      </div> -->
  </slot>
</template>
